import type { IBaseForm } from '@/types';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import { Button, Checkbox, Flex, Link } from '@chakra-ui/react';
import type { FieldPath, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { consents } from '../../constants';
import type { ISummaryFormFields } from '../../types';

export const SummaryForm: React.FC<IBaseForm<ISummaryFormFields>> = ({
  defaultValues,
  isBackVisible = true,
  onBack,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const {
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<ISummaryFormFields>({ defaultValues });

  const { i18n, t } = useTranslation();

  const handleSubmit: SubmitHandler<ISummaryFormFields> = (data, event): void => {
    event?.preventDefault();
    providedOnSubmit?.(data);
  };

  const handleBack = (): void => {
    onBack?.();
  };

  const requiredFields = [...consents.filter((c) => c.required === true).map((c) => c.id)];

  const areRequiredFieldsEmpty = hasEmptyFields<
    ISummaryFormFields,
    Extract<keyof ISummaryFormFields, string>
  >(watch(), requiredFields as Extract<keyof ISummaryFormFields, string>[]);

  const isDisabled = areRequiredFieldsEmpty || hasFormAnyError(errors);

  const labels: NodeListOf<HTMLElement> | null =
    document.querySelectorAll('.chakra-checkbox__label');

  labels.forEach((label) => (label.style.marginTop = '-5px'));

  return (
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    <form id="summaryForm" name="summaryForm" onSubmit={submitHandler(handleSubmit)}>
      <Flex direction="column" gap={8} mb={8}>
        {consents.map((c) => {
          const localizedHref = c.href?.replace('{lang}', i18n.language || 'en');
          return (
            <Checkbox
              alignItems="flex-start"
              key={c.id}
              spacing={4}
              variant="mbe"
              {...register(c.id as FieldPath<ISummaryFormFields>, { required: c.required })}
            >
              {
                <Trans
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  components={{
                    url: <Link color="brand.red.light" href={localizedHref} target="_blank" />,
                  }}
                  i18nKey={c.label}
                />
              }
            </Checkbox>
          );
        })}
      </Flex>
      <Flex flexDirection="row-reverse" gap={2} justifyContent="space-between">
        <Button disabled={isDisabled} type="submit" variant="mbePrimary">
          {t('ACTIONS.CONFIRM')}
        </Button>
        {isBackVisible && (
          <Button color="brand.red.light" variant="link" onClick={handleBack}>
            {t('ACTIONS.PREV')}
          </Button>
        )}
      </Flex>
    </form>
  );
};
